import { CampaignStatus } from './model';
import type { PersistedTargeting } from '../../targeting/model';
import { TargetingSupplyType } from '../../targeting/model';
import { numberFormat } from '../../../lib/format';
import { dashboardT } from '../../../lib/messages';

export type MadBuyLineItemTargeting = PersistedTargeting & {
  contentChannelsIncluded?: boolean | null | undefined;
  contentChannels?: string[] | undefined;
  contentChannelGroupsIncluded?: boolean | null | undefined;
  contentChannelGroups?: string[] | undefined;
  contentTitlesIncluded?: boolean | null | undefined;
  contentTitles?: string[] | undefined;
  contentKeywordsIncluded?: boolean | null | undefined;
  contentKeywords?: string[] | undefined;
  contentGenresIncluded?: boolean | null | undefined;
  contentGenres?: string[] | undefined;
};

type LegacyContextualTargetingKey =
  | 'includedChannels'
  | 'excludedChannels'
  | 'includedChannelGroups'
  | 'excludedChannelGroups'
  | 'includedContentTitles'
  | 'excludedContentTitles'
  | 'includedContentKeywords'
  | 'excludedContentKeywords'
  | 'includedContentGenres'
  | 'excludedContentGenres';

export type MadBuyLineItemTargetingMutationPayload = Omit<
  PersistedTargeting,
  LegacyContextualTargetingKey | 'userLanguages' | 'zipCodes'
> & {
  userLanguages: {
    id?: string | undefined;
    name: string;
    isoCode2?: string | undefined;
    isoCode3?: string | undefined;
  }[];
  contentChannelsIncluded: boolean;
  contentChannels: string[];
  contentChannelGroupsIncluded: boolean;
  contentChannelGroups: string[];
  contentTitlesIncluded: boolean;
  contentTitles: string[];
  contentKeywordsIncluded: boolean;
  contentKeywords: string[];
  contentGenresIncluded: boolean;
  contentGenres: string[];
};

export type MadBuyNamedEntity = {
  id: string;
  name: string;
};

export enum MadBuyLineItemDeliveryStatus {
  Delivering = 'DELIVERING',
  GoalMet = 'GOAL_MET',
  Completed = 'COMPLETED',
  Ready = 'READY',
}

export enum MadBuyLineItemType {
  Display = 'DISPLAY',
  Video = 'VIDEO',
  Audio = 'AUDIO',
}

export enum MadBuyLineItemPacing {
  Evenly = 'EVENLY',
  FrontLoaded = 'FRONT_LOADED',
  Asap = 'ASAP',
}

export enum MadBuyFrequencyCappingTimeUnit {
  Hour = 'HOUR',
  Day = 'DAY',
}

export { TargetingSupplyType as MadBuyLineItemSupplyType };

export enum MadBuyCreativeType {
  Display = 'DISPLAY',
  Audio = 'AUDIO',
  MediaFile = 'MEDIAFILE',
}

export const madBuyLineItemDeliveryStatusLabels: Record<MadBuyLineItemDeliveryStatus, string> = {
  get [MadBuyLineItemDeliveryStatus.Delivering]() {
    return dashboardT('madBuy.lineItems.delivering');
  },
  get [MadBuyLineItemDeliveryStatus.GoalMet]() {
    return dashboardT('madBuy.lineItems.goalMet');
  },
  get [MadBuyLineItemDeliveryStatus.Completed]() {
    return dashboardT('madBuy.common.completed');
  },
  get [MadBuyLineItemDeliveryStatus.Ready]() {
    return dashboardT('madBuy.lineItems.ready');
  },
};

export const madBuyLineItemDeliveryStatusFilterOptions = [
  MadBuyLineItemDeliveryStatus.Delivering,
  MadBuyLineItemDeliveryStatus.GoalMet,
  MadBuyLineItemDeliveryStatus.Completed,
  MadBuyLineItemDeliveryStatus.Ready,
] as const;

export const madBuyLineItemTypeLabels: Record<MadBuyLineItemType, string> = {
  get [MadBuyLineItemType.Display]() {
    return dashboardT('madBuy.common.display');
  },
  get [MadBuyLineItemType.Video]() {
    return dashboardT('madBuy.common.video');
  },
  get [MadBuyLineItemType.Audio]() {
    return dashboardT('madBuy.common.audio');
  },
};

export const madBuyLineItemTypeOptions = [
  MadBuyLineItemType.Display,
  MadBuyLineItemType.Video,
  MadBuyLineItemType.Audio,
] as const;

export const madBuyLineItemPacingLabels: Record<MadBuyLineItemPacing, string> = {
  get [MadBuyLineItemPacing.Evenly]() {
    return dashboardT('madBuy.lineItems.evenly');
  },
  get [MadBuyLineItemPacing.FrontLoaded]() {
    return dashboardT('madBuy.lineItems.frontloaded');
  },
  get [MadBuyLineItemPacing.Asap]() {
    return dashboardT('madBuy.lineItems.asap');
  },
};

export const madBuyLineItemPacingOptions = [
  MadBuyLineItemPacing.Evenly,
  MadBuyLineItemPacing.FrontLoaded,
  MadBuyLineItemPacing.Asap,
] as const;

export const madBuyLineItemSupplyTypeLabels: Record<TargetingSupplyType, string> = {
  get [TargetingSupplyType.All]() {
    return dashboardT('madBuy.lineItems.all');
  },
  get [TargetingSupplyType.Apps]() {
    return dashboardT('madBuy.common.apps');
  },
  get [TargetingSupplyType.Domains]() {
    return dashboardT('madBuy.common.domains');
  },
};

export const madBuyLineItemSupplyTypeOptions = [
  TargetingSupplyType.Apps,
  TargetingSupplyType.Domains,
] as const;

export const madBuyLineItemWeekDays = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

export type MadBuyLineItemWeekDay = (typeof madBuyLineItemWeekDays)[number];

export type MadBuyLineItem = {
  id: string;
  altId?: string | undefined;
  name: string;
  status?: CampaignStatus | string | undefined;
  systemStatus?: CampaignStatus | string | undefined;
  deliveryStatus?: MadBuyLineItemDeliveryStatus | string | undefined;
  adType?: MadBuyLineItemType | string | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  scheduling?: boolean | undefined;
  pacing?: MadBuyLineItemPacing | string | undefined;
  pacingPerHour?: MadBuyLineItemPacing | string | undefined;
  dayTimeActivities?: MadBuyLineItemDayTimeActivity[] | undefined;
  cpm?: number | undefined;
  domain?: string | undefined;
  margin?: number | undefined;
  deliveredImpressions?: number | undefined;
  impressionsGoal?: number | undefined;
  deliveredImpressionsPct?: number | undefined;
  upstreamCpmCapEnabled?: boolean | undefined;
  upstreamCpmCapMinCpm?: number | undefined;
  upstreamCpmCapMaxCpm?: number | undefined;
  impressionTrackers?: string[] | undefined;
  frequencyCapping?: MadBuyLineItemFrequencyCapping | undefined;
  targeting?: MadBuyLineItemTargeting | undefined;
  lineItemCreatives?: MadBuyCreativeAssociation[] | undefined;
  creatives?: MadBuyCreativeAssociation[] | undefined;
  conversionPixelId?: string | null | undefined;
  conversionPixel?: MadBuyConversionPixel | null | undefined;
  campaign?:
    | {
        id?: string | undefined;
        brand?:
          | {
              id?: string | undefined;
              name?: string | undefined;
            }
          | undefined;
      }
    | undefined;
};

export type MadBuyLineItemsPage = {
  content: MadBuyLineItem[];
  totalElements: number;
  totalPages?: number | undefined;
  number?: number | undefined;
  size?: number | undefined;
};

export type MadBuyLineItemsQuery = {
  campaignId: string;
  name: string;
  page: number;
  size: number;
  sortProperty: readonly string[];
  sortDirection: 'ASC' | 'DESC';
  status: CampaignStatus[];
  deliveryStatus: MadBuyLineItemDeliveryStatus[];
};

export type MadBuyLineItemFrequencyCapping = {
  fcActive: boolean;
  fcDuration: number;
  fcLimit: number;
  fcTimeUnit: MadBuyFrequencyCappingTimeUnit;
};

export type MadBuyLineItemDayTimeActivity = {
  day: MadBuyLineItemWeekDay;
  restrictedHours: boolean;
  startHour: number;
  endHour: number;
};

export type MadBuyCreative = MadBuyNamedEntity & {
  type?: MadBuyCreativeType | string | undefined;
  altId?: string | undefined;
};

export type MadBuyCreativeAssociation = {
  id?: string | undefined;
  creative: MadBuyCreative;
  weight: number;
  startDate?: string | undefined;
  endDate?: string | undefined;
};

export type MadBuyConversionPixel = MadBuyNamedEntity & {
  altId?: string | undefined;
  esid: string;
  triggerType?: string | undefined;
  type?: string | undefined;
  countType?: string | undefined;
  attributionWindowType?: string | undefined;
  attributionWindowValue?: number | undefined;
};

export type MadBuyLineItemMutationPayload = {
  name: string;
  status: CampaignStatus;
  adType: MadBuyLineItemType;
  cpm: number;
  impressionsGoal: number;
  campaign: { id: string };
  domain: string;
  margin: number | null;
  upstreamCpmCapEnabled: boolean;
  upstreamCpmCapMinCpm: number | null;
  upstreamCpmCapMaxCpm: number | null;
  impressionTrackers: string[];
  frequencyCapping: MadBuyLineItemFrequencyCapping;
  startDate: string;
  endDate: string;
  scheduling: boolean;
  pacing: MadBuyLineItemPacing;
  pacingPerHour: MadBuyLineItemPacing;
  dayTimeActivities: MadBuyLineItemDayTimeActivity[];
  targeting: MadBuyLineItemTargetingMutationPayload;
  lineItemCreatives: MadBuyCreativeAssociation[];
  conversionPixelId?: string | null | undefined;
};

export const defaultMadBuyLineItemsQuery = {
  campaignId: '',
  name: '',
  page: 0,
  size: 25,
  sortProperty: ['createdOn'],
  sortDirection: 'DESC',
  status: [CampaignStatus.Active],
  deliveryStatus: [],
} satisfies MadBuyLineItemsQuery;

export function resolveMadBuyLineItemDeliveryStatusLabel(status: string | undefined) {
  if (isMadBuyLineItemDeliveryStatus(status)) {
    return madBuyLineItemDeliveryStatusLabels[status];
  }

  return status ? titleCaseStatus(status) : dashboardT('madBuy.common.unknown');
}

export function mapMadBuyLineItemAdTypeToCreativeType(adType: MadBuyLineItemType) {
  switch (adType) {
    case MadBuyLineItemType.Audio:
      return MadBuyCreativeType.Audio;
    case MadBuyLineItemType.Video:
      return MadBuyCreativeType.MediaFile;
    case MadBuyLineItemType.Display:
    default:
      return MadBuyCreativeType.Display;
  }
}

export function formatMadBuyLineItemCurrency(
  value: number | undefined,
  options: { currency?: string } = {},
) {
  if (value === undefined || !Number.isFinite(value)) {
    return '-';
  }

  const format = (currency: string) =>
    numberFormat({
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  try {
    return format(options.currency ?? 'USD');
  } catch {
    // An unknown currency code makes Intl throw.
    return format('USD');
  }
}

export function resolveMadBuyLineItemCompletionPercent(lineItem: MadBuyLineItem) {
  if (typeof lineItem.deliveredImpressionsPct === 'number') {
    return clampPercent(lineItem.deliveredImpressionsPct * 100);
  }

  if (
    typeof lineItem.deliveredImpressions === 'number' &&
    typeof lineItem.impressionsGoal === 'number' &&
    lineItem.impressionsGoal > 0
  ) {
    return clampPercent((lineItem.deliveredImpressions / lineItem.impressionsGoal) * 100);
  }

  return 0;
}

export function serializeMadBuyLineItemsQuery(query: MadBuyLineItemsQuery) {
  const searchParams = new URLSearchParams();
  searchParams.set('campaignId', query.campaignId);
  searchParams.set('name', query.name);
  searchParams.set('page', String(query.page));
  searchParams.set('size', String(query.size));
  searchParams.set('sortProperty', query.sortProperty.join(','));
  searchParams.set('sortDirection', query.sortDirection);
  searchParams.set('status', query.status.join(','));
  if (query.deliveryStatus.length > 0) {
    searchParams.set('deliveryStatus', query.deliveryStatus.join(','));
  }

  return searchParams;
}

function isMadBuyLineItemDeliveryStatus(
  status: string | undefined,
): status is MadBuyLineItemDeliveryStatus {
  return Object.values(MadBuyLineItemDeliveryStatus).includes(
    status as MadBuyLineItemDeliveryStatus,
  );
}

function clampPercent(value: number) {
  return Math.min(100, Math.max(0, value));
}

function titleCaseStatus(status: string) {
  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}
